import React, { useState, useMemo, useEffect } from "react";
import { Loader2 } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    formatTotal,
    calculateDiscountedPrice,
    getProductAvailability,
} from "@/lib/utils";
import { fetchProductStock, fetchProductFamily } from "@/lib/api";

// ----------------------------------------------------------------------------

export interface VariantProduct {
    code: string;
    name?: string;
    price: string;
    numericPrice: number;
    discountPercent?: number;
    discountedPrice?: number;
    imageUrl?: string;
    uid?: number;
    sku?: string;
    colors?: string[];
    temperatures?: string[];
    sizes?: string[];
    tags?: string[];
    stockCount?: number;
    unlimited?: number;
}

export interface VariantSelection {
    color: string;
    temperature: string;
    size: string;
    quantity: number;
    note?: string;
}

interface ProductVariantModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    product: VariantProduct | null;
    onConfirm: (product: VariantProduct, selection: VariantSelection) => void;
}

// ----------------------------------------------------------------------------
// Color Map & Styles (from Storeden template/product.html)
// ----------------------------------------------------------------------------

const COLOR_HEX_MAP: Record<string, string> = {
    bianco: "#FFFFFF",
    "bianco opaco": "#F5F5F5",
    "bianco opale": "#F0F4F8",
    nero: "#18181B",
    "nero opaco": "#27272A",
    oro: "#D4AF37",
    "oro spazzolato": "#D4AF37",
    "oro specchiato": "#E5C158",
    ottone: "#C5A059",
    "ottone spazzolato": "#C5A059",
    brunito: "#5A3825",
    bronzo: "#8C6239",
    rame: "#B87333",
    "rame specchiato": "#D67A4F",
    cromo: "#E2E8F0",
    "cromo lucido": "#E2E8F0",
    "cromo specchiato": "#E2E8F0",
    nichel: "#B3B3B3",
    "nichel satinato": "#C0C0C0",
    titanio: "#878681",
    alluminio: "#A8A8A8",
    "alluminio spazzolato": "#B0B0B0",
    argento: "#D1D5DB",
    "argento opaco": "#CBD5E1",
    antracite: "#374151",
    grigio: "#6B7280",
    ruggine: "#8B3103",
    corten: "#843B21",
    marrone: "#5C4033",
    sabbia: "#D8C7A9",
    tortora: "#9E8B7D",
    trasparente: "#E2E8F0",
    ambra: "#F59E0B",
    fume: "#4B5563",
    fumé: "#4B5563",
    "fume' trasparente": "#64748B",
    verde: "#16A34A",
    "verde trasparente": "#22C55E",
    blu: "#2563EB",
    "blu trasparente": "#3B82F6",
    rosso: "#DC2626",
    giallo: "#EAB308",
};

function resolveColorStyle(colorRaw: string): {
    background: string;
    border: string;
    isLight: boolean;
} {
    const clean = colorRaw
        .toLowerCase()
        .replace(/^colore-/, "")
        .trim();
    if (clean.includes("/")) {
        const parts = clean.split("/").map((p) => p.trim());
        const p1 = parts[0] || "";
        const p2 = parts[1] || "";
        const hex1 = COLOR_HEX_MAP[p1] || "#CCCCCC";
        const hex2 = COLOR_HEX_MAP[p2] || "#CCCCCC";
        return {
            background: `linear-gradient(135deg, ${hex1} 50%, ${hex2} 50%)`,
            border: "rgba(0,0,0,0.15)",
            isLight: false,
        };
    }
    const hex = COLOR_HEX_MAP[clean] || "#CCCCCC";
    const isLight =
        hex === "#FFFFFF" ||
        hex === "#F5F5F5" ||
        hex === "#F0F4F8" ||
        hex === "#E2E8F0" ||
        hex === "#CBD5E1" ||
        hex === "#D1D5DB";
    return {
        background: hex,
        border: isLight ? "rgba(0,0,0,0.2)" : "transparent",
        isLight,
    };
}

function formatColorLabel(colorRaw: string): string {
    const clean = colorRaw.replace(/^colore-/, "").trim();
    if (!clean) return "Standard";
    return clean
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

// ----------------------------------------------------------------------------
// Dimension / Size Order & Labels (P -> M -> G -> S -> X)
// ----------------------------------------------------------------------------

const SIZE_ORDER = ["p", "m", "g", "s", "x"];
const SIZE_LABELS: Record<string, string> = {
    p: "Piccola (P)",
    m: "Media (M)",
    g: "Grande (G)",
    s: "Super (S)",
    x: "Extreme (X)",
};

function formatSizeLabel(sizeRaw: string): string {
    const lower = sizeRaw
        .toLowerCase()
        .replace(/^(dimensione-|taglia-|misura-)/, "")
        .trim();
    if (!lower) return "Standard";
    return (
        SIZE_LABELS[lower] || sizeRaw.charAt(0).toUpperCase() + sizeRaw.slice(1)
    );
}

function formatTempLabel(tempRaw: string): string {
    const clean = tempRaw
        .replace(/^(temperatura-colore-|temperatura-)/, "")
        .trim()
        .toUpperCase();
    return clean || "Standard";
}

// ----------------------------------------------------------------------------
// Tag Extraction Helpers
// ----------------------------------------------------------------------------

function extractColorTag(tags?: string[]): string {
    if (!tags) return "";
    for (const t of tags) {
        const lower = t.toLowerCase().trim();
        if (lower.startsWith("colore-")) {
            return lower.replace("colore-", "").trim();
        }
    }
    return "";
}

function extractSizeTag(tags?: string[]): string {
    if (!tags) return "";
    for (const t of tags) {
        const lower = t.toLowerCase().trim();
        if (
            lower.startsWith("dimensione-") ||
            lower.startsWith("taglia-") ||
            lower.startsWith("misura-")
        ) {
            return lower.replace(/^(dimensione-|taglia-|misura-)/, "").trim();
        }
    }
    return "";
}

function extractTempTag(tags?: string[]): string {
    if (!tags) return "";
    for (const t of tags) {
        const lower = t.toLowerCase().trim();
        if (lower.startsWith("temperatura-colore-")) {
            return lower
                .replace("temperatura-colore-", "")
                .trim()
                .toLowerCase();
        }
        if (lower.startsWith("temperatura-")) {
            return lower.replace("temperatura-", "").trim().toLowerCase();
        }
        if (/\b(2700k|3000k|4000k|5000k|6000k)\b/i.test(lower)) {
            const match = lower.match(/\b(2700k|3000k|4000k|5000k|6000k)\b/i);
            if (match && match[1]) return match[1].toLowerCase();
        }
    }
    return "";
}

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

export function ProductVariantModal(props: ProductVariantModalProps) {
    const { isOpen, onOpenChange, product, onConfirm } = props;

    // -------------------------------------
    // Family detection & Sibling fetching
    // -------------------------------------

    const fatherTag = useMemo(() => {
        if (!product?.tags) return null;
        return (
            product.tags
                .find((t) => t.toLowerCase().startsWith("art-padre-"))
                ?.toLowerCase() || null
        );
    }, [product?.tags]);

    const [familyProducts, setFamilyProducts] = useState<VariantProduct[]>([]);
    const [isLoadingFamily, setIsLoadingFamily] = useState(false);

    useEffect(() => {
        if (!isOpen || !fatherTag) {
            setFamilyProducts([]);
            setIsLoadingFamily(false);
            return;
        }

        let isMounted = true;
        setIsLoadingFamily(true);

        void fetchProductFamily(fatherTag)
            .then((siblings) => {
                if (!isMounted) return;
                if (siblings && siblings.length > 0) {
                    setFamilyProducts(siblings);
                } else {
                    setFamilyProducts([]);
                }
            })
            .catch((err) => {
                console.warn(
                    "[ProductVariantModal] Failed to load family:",
                    err,
                );
                if (isMounted) setFamilyProducts([]);
            })
            .finally(() => {
                if (isMounted) setIsLoadingFamily(false);
            });

        return () => {
            isMounted = false;
        };
    }, [isOpen, fatherTag]);

    const isFamily = Boolean(fatherTag && familyProducts.length > 1);

    // -------------------------------------
    // Distinct options (Family or Single Product)
    // -------------------------------------

    const displayColors = useMemo(() => {
        if (isFamily) {
            const seen = new Set<string>();
            const list: string[] = [];
            for (const p of familyProducts) {
                const c = extractColorTag(p.tags);
                if (c && !seen.has(c)) {
                    seen.add(c);
                    list.push(c);
                }
            }
            if (list.length > 0) return list;
        }

        // Single product fallback: use existing color from tag or product.colors
        if (!product) return [];
        const fromTag = extractColorTag(product.tags);
        if (fromTag) return [fromTag];
        if (product.colors && product.colors.length > 0) {
            return product.colors;
        }
        return [];
    }, [isFamily, familyProducts, product]);

    const displaySizes = useMemo(() => {
        if (isFamily) {
            const set = new Set<string>();
            for (const p of familyProducts) {
                const s = extractSizeTag(p.tags);
                if (s) set.add(s);
            }
            const sorted = SIZE_ORDER.filter((letter) => set.has(letter));
            const others = Array.from(set).filter(
                (s) => !SIZE_ORDER.includes(s),
            );
            const list = [...sorted, ...others];
            if (list.length > 0) return list;
        }

        // Single product fallback: use existing size from tag or product.sizes
        if (!product) return [];
        const fromTag = extractSizeTag(product.tags);
        if (fromTag) return [fromTag];
        if (product.sizes && product.sizes.length > 0) {
            const set = new Set<string>(product.sizes);
            const sorted = SIZE_ORDER.filter((letter) => set.has(letter));
            const others = Array.from(set).filter(
                (s) => !SIZE_ORDER.includes(s),
            );
            return [...sorted, ...others];
        }
        return [];
    }, [isFamily, familyProducts, product]);

    const displayTemps = useMemo(() => {
        if (isFamily) {
            const seen = new Set<string>();
            const list: string[] = [];
            for (const p of familyProducts) {
                const t = extractTempTag(p.tags);
                if (t && !seen.has(t)) {
                    seen.add(t);
                    list.push(t);
                }
            }
            if (list.length > 0) return list;
        }

        // Single product fallback: use existing temp from tag or product.temperatures
        if (!product) return [];
        const fromTag = extractTempTag(product.tags);
        if (fromTag) return [fromTag];
        if (product.temperatures && product.temperatures.length > 0) {
            return product.temperatures;
        }
        return [];
    }, [isFamily, familyProducts, product]);

    // -------------------------------------
    // Selection State
    // -------------------------------------

    const [color, setColor] = useState("");
    const [temperature, setTemperature] = useState("");
    const [size, setSize] = useState("");
    const [quantity, setQuantity] = useState("1");
    const [note, setNote] = useState("");

    // Track initial setup when modal opens
    const prevOpenRef = React.useRef(false);
    const prevCodeRef = React.useRef<string | undefined>(undefined);

    useEffect(() => {
        const isOpening = isOpen && !prevOpenRef.current;
        const isDifferentProduct = product?.code !== prevCodeRef.current;

        if (isOpen && product && (isOpening || isDifferentProduct)) {
            const initialColor =
                extractColorTag(product.tags) || product.colors?.[0] || "";
            const initialSize =
                extractSizeTag(product.tags) || product.sizes?.[0] || "";
            const initialTemp =
                extractTempTag(product.tags) || product.temperatures?.[0] || "";

            setColor(initialColor);
            setSize(initialSize);
            setTemperature(initialTemp);
            setQuantity("1");
            setNote("");
        }

        prevOpenRef.current = isOpen;
        prevCodeRef.current = product?.code;
    }, [isOpen, product]);

    // Keep selection synchronized with available options
    useEffect(() => {
        if (
            displayColors.length > 0 &&
            (!color || !displayColors.includes(color))
        ) {
            setColor(displayColors[0] || "");
        }
    }, [displayColors, color]);

    useEffect(() => {
        if (
            displaySizes.length > 0 &&
            (!size || !displaySizes.includes(size))
        ) {
            setSize(displaySizes[0] || "");
        }
    }, [displaySizes, size]);

    useEffect(() => {
        if (
            displayTemps.length > 0 &&
            (!temperature || !displayTemps.includes(temperature))
        ) {
            setTemperature(displayTemps[0] || "");
        }
    }, [displayTemps, temperature]);

    // When familyProducts finishes loading, ensure selection matches a valid sibling
    useEffect(() => {
        if (isFamily && familyProducts.length > 0) {
            const exactMatch = familyProducts.find((p) => {
                const c = extractColorTag(p.tags);
                const s = extractSizeTag(p.tags);
                const t = extractTempTag(p.tags);
                return (
                    (!color || c === color) &&
                    (!size || s === size) &&
                    (!temperature || t === temperature)
                );
            });

            if (!exactMatch) {
                const currentInFamily =
                    familyProducts.find((p) => p.code === product?.code) ||
                    familyProducts[0];
                if (currentInFamily) {
                    setColor(extractColorTag(currentInFamily.tags));
                    setSize(extractSizeTag(currentInFamily.tags));
                    setTemperature(extractTempTag(currentInFamily.tags));
                }
            }
        }
    }, [isFamily, familyProducts, product?.code]);

    // -------------------------------------
    // Active Sibling Resolution
    // -------------------------------------

    const activeProduct = useMemo(() => {
        if (!isFamily || familyProducts.length === 0) {
            return product;
        }

        const match = familyProducts.find((p) => {
            const c = extractColorTag(p.tags);
            const s = extractSizeTag(p.tags);
            const t = extractTempTag(p.tags);

            const matchColor = !color || c === color;
            const matchSize = !size || s === size;
            const matchTemp = !temperature || t === temperature;

            return matchColor && matchSize && matchTemp;
        });

        if (!match) return product;

        return {
            ...match,
            discountPercent: product?.discountPercent,
            discountedPrice:
                product?.discountPercent && product.discountPercent > 0
                    ? calculateDiscountedPrice(
                          match.numericPrice,
                          product.discountPercent,
                      )
                    : undefined,
        };
    }, [isFamily, familyProducts, color, size, temperature, product]);

    // -------------------------------------
    // Live Stock & Availability
    // -------------------------------------

    const [liveStockCount, setLiveStockCount] = useState<number | undefined>(
        activeProduct?.stockCount,
    );

    useEffect(() => {
        setLiveStockCount(activeProduct?.stockCount);
        const identifier = activeProduct?.code || activeProduct?.uid;
        if (
            activeProduct &&
            activeProduct.stockCount === undefined &&
            identifier
        ) {
            void fetchProductStock(identifier).then((res) => {
                if (res && typeof res.stockCount === "number") {
                    setLiveStockCount(res.stockCount);
                }
            });
        }
    }, [activeProduct?.uid, activeProduct?.code, activeProduct?.stockCount]);

    const effectiveProduct = useMemo(() => {
        if (!activeProduct) return null;
        return {
            ...activeProduct,
            stockCount: liveStockCount ?? activeProduct.stockCount,
        };
    }, [activeProduct, liveStockCount]);

    const availability = useMemo(
        () => getProductAvailability(effectiveProduct),
        [effectiveProduct],
    );

    const isUnlimited =
        !availability.isOutOfCatalog &&
        (effectiveProduct?.unlimited === 1 ||
            (effectiveProduct as any)?.unlimited === true);
    const isRestocking =
        availability.isRestocking || availability.caseType === "restocking";
    const currentStock =
        availability.availableQuantity ??
        effectiveProduct?.stockCount ??
        liveStockCount ??
        activeProduct?.stockCount;
    const numericStock =
        typeof currentStock === "number"
            ? currentStock
            : currentStock !== undefined && currentStock !== null
              ? Number(currentStock)
              : undefined;

    const hasStock =
        isUnlimited ||
        isRestocking ||
        (numericStock !== undefined
            ? numericStock > 0
            : availability.canOrder && availability.availableQuantity !== 0);

    const canActuallyOrder = availability.canOrder && hasStock;

    // -------------------------------------
    // Handlers with smart adjustment for families
    // -------------------------------------

    const handleSelectColor = (newColor: string) => {
        setColor(newColor);
        if (isFamily) {
            const matchWithCurrentSize = familyProducts.some((p) => {
                return (
                    extractColorTag(p.tags) === newColor &&
                    extractSizeTag(p.tags) === size &&
                    (!temperature || extractTempTag(p.tags) === temperature)
                );
            });
            if (!matchWithCurrentSize) {
                const firstValid = familyProducts.find(
                    (p) => extractColorTag(p.tags) === newColor,
                );
                if (firstValid) {
                    setSize(extractSizeTag(firstValid.tags));
                    if (extractTempTag(firstValid.tags)) {
                        setTemperature(extractTempTag(firstValid.tags));
                    }
                }
            }
        }
    };

    const handleSelectSize = (newSize: string) => {
        setSize(newSize);
        if (isFamily) {
            const matchWithCurrentColor = familyProducts.some((p) => {
                return (
                    extractSizeTag(p.tags) === newSize &&
                    extractColorTag(p.tags) === color &&
                    (!temperature || extractTempTag(p.tags) === temperature)
                );
            });
            if (!matchWithCurrentColor) {
                const firstValid = familyProducts.find(
                    (p) => extractSizeTag(p.tags) === newSize,
                );
                if (firstValid) {
                    setColor(extractColorTag(firstValid.tags));
                    if (extractTempTag(firstValid.tags)) {
                        setTemperature(extractTempTag(firstValid.tags));
                    }
                }
            }
        }
    };

    const handleSelectTemp = (newTemp: string) => {
        setTemperature(newTemp);
        if (isFamily) {
            const matchWithCurrent = familyProducts.some((p) => {
                return (
                    extractTempTag(p.tags) === newTemp &&
                    extractColorTag(p.tags) === color &&
                    extractSizeTag(p.tags) === size
                );
            });
            if (!matchWithCurrent) {
                const firstValid = familyProducts.find(
                    (p) => extractTempTag(p.tags) === newTemp,
                );
                if (firstValid) {
                    setColor(extractColorTag(firstValid.tags));
                    setSize(extractSizeTag(firstValid.tags));
                }
            }
        }
    };

    // -------------------------------------
    // Quantity options
    // -------------------------------------

    const quantityOptions = useMemo(() => {
        if (!canActuallyOrder) return [0];
        const max = availability.availableQuantity;
        if (typeof max === "number" && max > 0) {
            if (max <= 10) {
                return Array.from({ length: max }, (_, i) => i + 1);
            }
            const standard = [1, 2, 3, 4, 5, 10, 20, 50, 100].filter(
                (n) => n < max,
            );
            return Array.from(new Set([...standard, max])).sort(
                (a, b) => a - b,
            );
        }
        return [1, 2, 3, 4, 5, 10, 20, 50, 100];
    }, [canActuallyOrder, availability.availableQuantity]);

    useEffect(() => {
        if (canActuallyOrder && quantity === "0") {
            setQuantity("1");
        }
    }, [canActuallyOrder, quantity]);

    // -------------------------------------
    // Actions
    // -------------------------------------

    const handleConfirm = () => {
        if (!effectiveProduct || !canActuallyOrder) return;
        onConfirm(effectiveProduct, {
            color: formatColorLabel(color),
            temperature: formatTempLabel(temperature),
            size: formatSizeLabel(size),
            quantity: parseInt(quantity, 10) || 1,
            note: note.trim() || undefined,
        });
    };

    // -------------------------------------
    // Render Guard
    // -------------------------------------

    if (!product) return null;

    const displayProduct = effectiveProduct || product;

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] sm:max-w-[480px] p-4 sm:p-6 gap-4 sm:gap-5 rounded-xl max-h-[92vh] overflow-y-auto">
                {/* Header with Product Info */}
                <div className="flex items-center gap-3 sm:gap-4 border-b border-gray-100 pb-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                        {displayProduct.imageUrl ? (
                            <img
                                src={displayProduct.imageUrl}
                                alt={displayProduct.code}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
                        )}
                    </div>
                    <div className="flex-1 flex justify-between items-start gap-3">
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                                    {displayProduct.code}
                                </h2>
                                {isLoadingFamily && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full font-medium">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Varianti
                                    </span>
                                )}
                            </div>
                            {displayProduct.name && (
                                <div className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                                    {displayProduct.name}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Availability Info Card */}
                <div
                    className={`p-3 rounded-lg border text-xs space-y-2 ${
                        availability.caseType === "available"
                            ? "bg-emerald-50/80 border-emerald-200 text-emerald-950"
                            : availability.caseType === "restocking"
                              ? "bg-amber-50/80 border-amber-200 text-amber-950"
                              : availability.caseType ===
                                  "out_of_catalog_with_stock"
                                ? "bg-orange-50/80 border-orange-200 text-orange-950"
                                : "bg-red-50/80 border-red-200 text-red-950"
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold flex items-center gap-1.5">
                            {availability.caseType === "restocking" && (
                                <span className="text-amber-950 flex opacity-85">
                                    Non disponibile
                                </span>
                            )}
                            {(availability.caseType ===
                                "out_of_catalog_with_stock" ||
                                availability.caseType ===
                                    "out_of_catalog_exhausted" ||
                                availability.isOutOfCatalog) && (
                                <span>Articolo fuori catalogo</span>
                            )}
                            {availability.caseType === "unavailable" &&
                                !availability.isOutOfCatalog && (
                                    <span>Articolo non disponibile</span>
                                )}
                            {availability.caseType === "available" && (
                                <span></span>
                            )}
                        </span>
                        <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-semibold text-xs border ${
                                availability.caseType === "available"
                                    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                    : availability.caseType === "restocking"
                                      ? "bg-amber-100 text-amber-950 border-amber-300"
                                      : availability.caseType ===
                                          "out_of_catalog_with_stock"
                                        ? "bg-orange-100 text-orange-950 border-orange-300"
                                        : "bg-red-100 text-red-800 border-red-300"
                            }`}>
                            <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                    availability.caseType === "available"
                                        ? "bg-emerald-600"
                                        : availability.caseType === "restocking"
                                          ? "bg-amber-600"
                                          : availability.caseType ===
                                              "out_of_catalog_with_stock"
                                            ? "bg-orange-600"
                                            : "bg-red-600"
                                }`}
                            />
                            {availability.statusLabel}
                        </span>
                    </div>

                    {(availability.caseType === "restocking" ||
                        availability.caseType === "out_of_catalog_with_stock" ||
                        availability.caseType === "available" ||
                        availability.notes) && (
                        <div className="space-y-1.5 pt-1.5 border-t border-black/5">
                            {availability.caseType === "restocking" && (
                                <>
                                    <div className="flex justify-between items-center">
                                        <span className="text-amber-950 flex opacity-85 items-center gap-1">
                                            Data presunto arrivo:
                                        </span>
                                        <span className="font-bold text-amber-950 flex opacity-85">
                                            {availability.restockDate}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-amber-950 flex opacity-85">
                                            Quantità ordinabile:
                                        </span>
                                        <span className="font-bold text-amber-950 opacity-85">
                                            {availability.restockQuantity}
                                        </span>
                                    </div>
                                    {availability.allRestockDates.length >
                                        1 && (
                                        <div className="pt-0.5 text-[11px] text-amber-900/80">
                                            Ulteriori arrivi programmati:{" "}
                                            {availability.allRestockDates
                                                .slice(1)
                                                .map(
                                                    (r) =>
                                                        `${r.date} (${r.quantity} pz)`,
                                                )
                                                .join(", ")}
                                        </div>
                                    )}
                                </>
                            )}

                            {availability.caseType ===
                                "out_of_catalog_with_stock" && (
                                <div className="flex justify-between items-center">
                                    <span className="text-orange-950 opacity-100">
                                        Quantità:
                                    </span>
                                    <span className="font-bold text-orange-950">
                                        {availability.availableQuantity ??
                                            displayProduct?.stockCount ??
                                            liveStockCount ??
                                            0}
                                    </span>
                                </div>
                            )}

                            {availability.caseType === "available" && (
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">
                                        Quantità:
                                    </span>
                                    <span className="font-bold text-emerald-950">
                                        {availability.availableQuantity !==
                                        undefined
                                            ? `${availability.availableQuantity}`
                                            : "Disponibilità immediata"}
                                    </span>
                                </div>
                            )}

                            {availability.notes && (
                                <p className="text-[11px] opacity-85 pt-0.5 leading-snug">
                                    {availability.notes}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Variant Selectors Section */}
                {canActuallyOrder ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-sm font-semibold uppercase text-gray-500 tracking-wider">
                                {displayColors.length > 0 ||
                                displaySizes.length > 0 ||
                                displayTemps.length > 0
                                    ? "Seleziona variante prodotto"
                                    : "Configura quantità ordine"}
                            </DialogTitle>
                        </DialogHeader>

                        {/* Selectors grid matching standard input controls */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                            {/* 1. Colore (Selettore) */}
                            {displayColors.length > 0 && (
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-gray-500 font-medium">
                                        Colore
                                    </Label>
                                    <Select
                                        value={color}
                                        onValueChange={handleSelectColor}>
                                        <SelectTrigger className="w-full bg-white border-gray-200 text-sm h-10">
                                            <SelectValue placeholder="Seleziona colore">
                                                {color ? (
                                                    <div className="flex items-center gap-2 truncate">
                                                        <span
                                                            className="w-3.5 h-3.5 rounded-full flex-shrink-0 shadow-2xs"
                                                            style={{
                                                                background:
                                                                    resolveColorStyle(
                                                                        color,
                                                                    )
                                                                        .background,
                                                                border: `1px solid ${resolveColorStyle(color).border}`,
                                                            }}
                                                        />
                                                        <span className="truncate">
                                                            {formatColorLabel(
                                                                color,
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    "Seleziona colore"
                                                )}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {displayColors.map((c) => {
                                                const style =
                                                    resolveColorStyle(c);
                                                return (
                                                    <SelectItem
                                                        key={c}
                                                        value={c}>
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="w-3.5 h-3.5 rounded-full flex-shrink-0 shadow-2xs"
                                                                style={{
                                                                    background:
                                                                        style.background,
                                                                    border: `1px solid ${style.border}`,
                                                                }}
                                                            />
                                                            <span>
                                                                {formatColorLabel(
                                                                    c,
                                                                )}
                                                            </span>
                                                        </div>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* 2. Dimensione / Taglia (Selettore) */}
                            {displaySizes.length > 0 && (
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-gray-500 font-medium">
                                        Dimensione / Taglia
                                    </Label>
                                    <Select
                                        value={size}
                                        onValueChange={handleSelectSize}>
                                        <SelectTrigger className="w-full bg-white border-gray-200 text-sm h-10">
                                            <SelectValue placeholder="Seleziona dimensione">
                                                {size
                                                    ? formatSizeLabel(size)
                                                    : "Seleziona dimensione"}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {displaySizes.map((s) => (
                                                <SelectItem key={s} value={s}>
                                                    {formatSizeLabel(s)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* 3. Temperatura Colore (Selettore) */}
                            {displayTemps.length > 0 && (
                                <div className="space-y-1.5">
                                    <Label className="text-xs text-gray-500 font-medium">
                                        Temperatura Colore
                                    </Label>
                                    <Select
                                        value={temperature}
                                        onValueChange={handleSelectTemp}>
                                        <SelectTrigger className="w-full bg-white border-gray-200 text-sm h-10">
                                            <SelectValue placeholder="Seleziona temperatura">
                                                {temperature
                                                    ? formatTempLabel(
                                                          temperature,
                                                      )
                                                    : "Seleziona temperatura"}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {displayTemps.map((t) => (
                                                <SelectItem key={t} value={t}>
                                                    {formatTempLabel(t)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* 4. Quantità (Selettore) */}
                            <div className="space-y-1.5">
                                <Label className="text-xs text-gray-500 font-medium">
                                    Quantità
                                </Label>
                                <Select
                                    value={quantity}
                                    onValueChange={setQuantity}>
                                    <SelectTrigger className="w-full bg-white border-gray-200 text-sm h-10">
                                        <SelectValue placeholder="1" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {quantityOptions.map((num) => (
                                            <SelectItem
                                                key={num}
                                                value={num.toString()}>
                                                {num}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Product Note */}
                        <div className="space-y-1.5 pt-1">
                            <Label
                                htmlFor="product-item-note"
                                className="text-xs text-gray-500 font-medium">
                                Note per questo articolo (opzionale)
                            </Label>
                            <textarea
                                id="product-item-note"
                                rows={2}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Es. Richiesta finitura speciale, consegna congiunta, note tecniche..."
                                className="w-full text-xs rounded-md border border-gray-200 bg-white p-2.5 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                            />
                        </div>
                    </>
                ) : (
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3.5 text-xs text-gray-600 space-y-1">
                        <p className="font-semibold text-gray-800">
                            Articolo non disponibile
                        </p>
                        <p className="text-gray-500">
                            {availability.isOutOfCatalog
                                ? "Questo prodotto è fuori catalogo ed esaurito a magazzino. Viene mostrato a solo scopo informativo."
                                : "Questo prodotto non è attualmente a magazzino. Viene mostrato a solo scopo informativo."}
                        </p>
                    </div>
                )}

                {/* Price Section */}
                <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                    <div>
                        <Label className="text-xs text-gray-500 font-medium block">
                            Prezzo unitario B2B
                        </Label>
                        {displayProduct.discountPercent !== undefined &&
                        displayProduct.discountPercent > 0 ? (
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-lg font-bold text-gray-900">
                                    {formatTotal(
                                        displayProduct.discountedPrice ??
                                            calculateDiscountedPrice(
                                                displayProduct.numericPrice,
                                                displayProduct.discountPercent,
                                            ),
                                    )}
                                </span>
                                <span className="text-xs text-gray-400 line-through">
                                    {formatTotal(displayProduct.numericPrice)}
                                </span>
                            </div>
                        ) : (
                            <span className="text-lg font-bold text-gray-900 mt-0.5 block">
                                {formatTotal(displayProduct.numericPrice)}
                            </span>
                        )}
                    </div>

                    <div className="text-right">
                        <span className="text-[11px] text-gray-400 block">
                            {isFamily ? "Codice variante" : "Codice articolo"}
                        </span>
                        <span className="text-xs font-semibold text-gray-700 font-mono">
                            {displayProduct.code}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                {canActuallyOrder ? (
                    <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-between pt-2">
                        <Button
                            variant="outline"
                            className="w-full sm:flex-1 border-gray-200 text-gray-700 hover:bg-gray-50"
                            onClick={() => onOpenChange(false)}>
                            Annulla
                        </Button>
                        <Button
                            className={`w-full sm:flex-1 text-white ${
                                availability.caseType === "restocking"
                                    ? "bg-amber-600 hover:bg-amber-700"
                                    : "bg-[#0D6EFD] hover:bg-blue-700"
                            }`}
                            onClick={handleConfirm}>
                            {availability.caseType === "restocking"
                                ? "Preordina prodotto"
                                : "Aggiungi all'ordine"}
                        </Button>
                    </DialogFooter>
                ) : (
                    <DialogFooter className="pt-2">
                        <Button
                            variant="outline"
                            className="w-full border-gray-200 text-gray-700 hover:bg-gray-50"
                            onClick={() => onOpenChange(false)}>
                            Chiudi
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
